import type { YardReceiptView } from '@chukua/common';
import { Button, Tag } from 'antd';
import type { useReceiveTruck } from '../hooks/use-receive-truck';
import { returnStatusTag } from '../policies/return-status.policy';
import { DefectList } from './DefectList';
import { ReadingsFields } from './ReadingsFields';
import { ReceiptNotices } from './ReceiptNotices';
import { ReceivedSummary } from './ReceivedSummary';

type Receiving = ReturnType<typeof useReceiveTruck>;

interface ReceiveTruckContentProps {
  view: YardReceiptView;
  receiving: Receiving;
}

/** A loaded receipt: the truck, what was said about it, its readings and defects, and the one action that receives it. */
export function ReceiveTruckContent({ view, receiving }: ReceiveTruckContentProps) {
  const isOpen = view.verdict === 'ready' || view.verdict === 'blocked';
  return (
    <>
      <TruckHeader view={view} />
      {view.received && <ReceivedSummary received={view.received} />}
      <ReceiptNotices refusal={view.refusal} blockers={receiving.blockers} warnings={view.warnings} submitError={receiving.refusal} />
      {view.verdict !== 'refused' && (
        <ReadingsFields
          readings={receiving.readings}
          onChange={receiving.setReadings}
          disabled={!isOpen || receiving.isReceiving}
          provenance={provenanceOf(view.check?.recordedBy)}
        />
      )}
      {view.check && <DefectList defects={view.check.defects} />}
      {isOpen && receiving.canReceive && (
        <Button
          type="primary"
          size="large"
          block
          loading={receiving.isReceiving}
          disabled={receiving.blockers.length > 0}
          onClick={() => void receiving.receive()}
        >
          Receive truck
        </Button>
      )}
    </>
  );
}

function TruckHeader({ view }: { view: YardReceiptView }) {
  const tag = returnStatusTag(view.status);
  return (
    <header>
      <h1 className="text-2xl font-semibold tracking-tight text-fg">{view.truckRegistration ?? 'No truck'}</h1>
      <p className="mt-1 text-sm text-muted">
        {view.number} · {view.customerName ?? 'No customer'} · {view.driverName ?? 'No driver'}
      </p>
      <Tag color={tag.color} className="mt-2">
        {tag.label}
      </Tag>
    </header>
  );
}

function provenanceOf(recordedBy: 'driver' | 'office' | undefined): string {
  if (recordedBy === 'driver') return "From the driver's post-trip check. Correct them if the gauge says otherwise.";
  if (recordedBy === 'office') return 'Entered at the yard.';
  return 'The driver recorded no post-trip check, so enter what the odometer and gauge read.';
}
